import { useParams } from "react-router-dom";
import { useEffect } from "react";
import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import PRODUCTS from "../SignUpPage/products";
import { productDetails } from "../SignUpPage/productDetails";
import "./ProductPage.scss";
import msraImg from "../../assets/course-cards/msra.jpg";
import interviewPackSt1 from "../../assets/course-cards/interview-pack-st1.jpg";
import FRCOphthPart1Page from "./FRCOphthPart1Page";
import FRCOphthPart2Page from "./FRCOphthPart2Page";
import InterviewPackPage from "./InterviewPackPage";
import MSRAPage from "./MSRAPage";
import DukeElderPage from "./DukeElderPage";
import PortfolioBuilderPage from "./PortfolioBuilderPage";
import InterviewCoachingPage from "./InterviewCoachingPage";

const ProductPage = () => {
  const { productId } = useParams<{ productId: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  if (!productId) {
    return (
      <>
        <NavBar />
        <div className="site-container" style={{ padding: "100px 20px", textAlign: "center" }}>
          <h1>Product Not Found</h1>
          <p>The product you're looking for doesn't exist.</p>
          <Button text="Go to Home" linkTo="/" />
        </div>
        <Footer />
      </>
    );
  }

  const product = PRODUCTS.find((p) => p.id === productId);

  if (!product) {
    return (
      <>
        <NavBar />
        <div className="site-container" style={{ padding: "100px 20px", textAlign: "center" }}>
          <h1>Product Not Found</h1>
          <p>The product you're looking for doesn't exist.</p>
          <Button text="Go to Home" linkTo="/" />
        </div>
        <Footer />
      </>
    );
  }

  // Route to specific product page components
  if (productId === "frcophth1") {
    return <FRCOphthPart1Page product={product} />;
  }

  if (productId === "frcophth2") {
    return <FRCOphthPart2Page product={product} />;
  }

  if (productId === "interview_pack_st1") {
    return <InterviewPackPage product={product} />;
  }

  if (productId === "msra") {
    return <MSRAPage product={product} />;
  }

  if (productId === "duke_elder") {
    return <DukeElderPage product={product} />;
  }

  if (productId === "portfolio_builder") {
    return <PortfolioBuilderPage product={product} />;
  }

  if (productId === "interview_coaching") {
    return <InterviewCoachingPage product={product} />;
  }

  // Default layout for other products
  const details = productDetails[productId];

  if (!details) {
    return (
      <>
        <NavBar />
        <div className="site-container" style={{ padding: "100px 20px", textAlign: "center" }}>
          <h1>Product Not Found</h1>
          <p>The product you're looking for doesn't exist.</p>
          <Button text="Go to Home" linkTo="/" />
        </div>
        <Footer />
      </>
    );
  }

  const getProductImage = () => {
    switch (productId) {
      case "msra":
        return msraImg;
      case "interview_pack_st1":
        return interviewPackSt1;
      default:
        return undefined;
    }
  };

  return (
    <>
      <NavBar />
      <div id="ProductPage">
        <div className="product-hero-section">
          <div className="product-hero-background">
            <div className="product-hero-left"></div>
            {getProductImage() && (
              <div 
                className="product-hero-image-bg"
                style={{
                  backgroundImage: `url(${getProductImage()})`
                }}
              ></div>
            )}
          </div>

          <div className="product-hero-overlay">
            <div className="site-container">
              <div className="product-hero-content">
                <h1>{product.title}</h1>
                <p className="product-description">{product.description}</p>
                <div className="product-pricing">
                  <span className="price">£{product.price}</span>
                  <span className="length">/ {product.length}</span>
                </div>
                {!product.comingSoon && (
                  <Button
                    text="Purchase Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="product-details-section">
          <div className="site-container">
            <div className="product-details-grid">
              <div className="product-main-content">
                <section className="product-section">
                  <h2>About the {details.examName}</h2>
                  <p>{details.examDescription}</p>
                  {details.fullDescription && (
                    <div style={{ marginTop: '20px' }}>
                      <p>{details.fullDescription}</p>
                    </div>
                  )}
                </section>

                <section className="product-section">
                  <h2>What's Included</h2>
                  <div className="included-features">
                    <div className="feature-item">
                      <div className="feature-icon">
                        <Checkmark />
                      </div>
                      <div className="feature-content">
                        <h3>{details.numberOfQuestions.toLocaleString()}+ Exam-Relevant MCQs</h3>
                        <p>
                          Comprehensive question bank covering all aspects of the {details.examName}
                        </p>
                        {details.highYieldQuestions && (
                          <p style={{ marginTop: '10px', fontWeight: 'bold', color: '#0e4f99' }}>
                            Plus {details.highYieldQuestions.toLocaleString()}+ high-yield exam-favourite questions
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="feature-item">
                      <div className="feature-icon">
                        <Checkmark />
                      </div>
                      <div className="feature-content">
                        <h3>Question Types</h3>
                        <ul className="question-types-list">
                          {details.questionTypes.map((type, index) => (
                            <li key={index}>{type}</li>
                          ))}
                        </ul>
                        {details.topicsCovered && details.topicsCovered.length > 0 && (
                          <div style={{ marginTop: '15px' }}>
                            <h4 style={{ marginBottom: '10px', fontSize: '1.1rem', fontWeight: 'bold' }}>Complete Syllabus Coverage:</h4>
                            <ul className="question-types-list">
                              {details.topicsCovered.map((topic, index) => (
                                <li key={index}>{topic}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="feature-item">
                      <div className="feature-icon">
                        <Checkmark />
                      </div>
                      <div className="feature-content">
                        <h3>Exam Information</h3>
                        <div className="exam-info">
                          <div className="exam-info-item">
                            <strong>Format:</strong> {details.examFormat}
                          </div>
                          {details.examDuration && (
                            <div className="exam-info-item">
                              <strong>Duration:</strong> {details.examDuration}
                            </div>
                          )}
                          {details.examDate && (
                            <div className="exam-info-item">
                              <strong>Schedule:</strong> {details.examDate}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="product-section">
                  <h2>Key Features</h2>
                  <ul className="key-features-list">
                    {details.keyFeatures.map((feature, index) => (
                      <li key={index}>
                        <Checkmark />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </section>

                {details.whoIsThisFor && details.whoIsThisFor.length > 0 && (
                  <section className="product-section">
                    <h2>Who Is This For?</h2>
                    <ul className="key-features-list">
                      {details.whoIsThisFor.map((item, index) => (
                        <li key={index}>
                          <Checkmark />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>

              <div className="product-sidebar">
                <div className="sidebar-card">
                  <h3>Product Details</h3>
                  <div className="sidebar-details">
                    <div className="detail-row">
                      <span className="detail-label">Price:</span>
                      <span className="detail-value">£{product.price}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Access Length:</span>
                      <span className="detail-value">{product.length}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Questions:</span>
                      <span className="detail-value">{details.numberOfQuestions.toLocaleString()}+</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Question Types:</span>
                      <span className="detail-value">{details.questionTypes.length}</span>
                    </div>
                  </div>
                  {!product.comingSoon && (
                    <div className="sidebar-cta">
                      <Button
                        text="Get Started"
                        linkTo={`/sign-up?product=${product.id}`}
                        type="navy"
                      />
                    </div>
                  )}
                </div>

                <div className="sidebar-card">
                  <h3>Exam Preparation</h3>
                  <p>
                    This product is designed to help you prepare for and ace the{" "}
                    <strong>{details.examName}</strong>. Our comprehensive question bank
                    covers all topics and question formats you'll encounter in the actual exam.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProductPage;
